import { useEffect, useState } from 'react'
import AssignPermissionSidebar from './AssignPermissionsSidebar'
import {
  DataTypeStore,
  DropdownType,
  getPermissionSetsForGlobal,
  getPermissionSetsList,
  getProgramsAndPermissionSets,
  hideLoader,
  PermissionSetType,
  ProgramPermissionsType,
  showLoader,
} from '@app-store'
import { AssignPermissionSetRightPanel } from './AssignPermissionSetRightPanel'
import {
  columnTypesConst,
  CustomConfirmationModal,
  CustomTable,
  SvgEdit,
  SvgEntity,
  SvgGlobalSet,
  TableColumnType,
  ToggleButton,
  Tooltip,
} from '@ui-kit'
import { PERMISSION_ASSIGNMENT } from '../../utils/strings'
import { PERMISSION_ASSIGNMENT as PERMISSION_CONSTANTS } from '../../utils/constants'
import { NAMESPACES, PERMISSION_MODULES } from '../../utils/constants'
import { isEmpty } from 'lodash'
import {
  AssignmentSetProps,
  ProgramPermissionSetTableType,
} from '../../types/permissionAssignment.types'
import { useParams } from 'react-router-dom'
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../../utils/agent.store'
import {
  canActAsAccessManagerOnPermissionAssignment,
  permissionObject,
  isUamPermissionSetDisabledForProfiles,
} from '../../utils/helperFunctions'
import { ShortcutKeys, TAB_INDEX_DEFAULT } from '../../constants/form.constants'

export const AssignPermissionSet = ({
  programPermissionSets,
  setProgramPermissionSets,
  actionMode,
  programOptions,
  isGlobalSet,
  setCheckedPermissionSets,
  checkedPermissionSets,
  targetUserProfiles = [],
}: AssignmentSetProps) => {
  const { userId } = useParams()
  const dispatch = useAppDispatch()
  const programPermissions = useAppSelector(
    (state: RootState) => state.clientPermissions.programPermissions
  )
  const canActAsAccessManagerOnPa = useAppSelector(
    canActAsAccessManagerOnPermissionAssignment
  )
  const selectedPermissionSets = Object.values(checkedPermissionSets)?.sort(
    (a, b) => a.permission_set_name?.localeCompare(b.permission_set_name)
  )
  const [tableData, setTableData] = useState<ProgramPermissionSetTableType[]>(
    []
  )
  const [selectedProgram, setSelectedProgram] = useState<{
    program_id: string
    program_name: string
  } | null>(null)
  const [allPermissionSets, setAllPermissionSets] = useState<
    PermissionSetType[]
  >([])
  const [rowInstance, setRowInstance] = useState<{
    row: DataTypeStore
    rowIndex: number
  } | null>(null)

  const [toggleModalOpen, setToggleModalOpen] = useState<boolean>(false)

  const handleClearChip = (value: number) => {
    setCheckedPermissionSets((prev) => {
      const updatedState = { ...prev }
      delete updatedState[value]
      return updatedState
    })
  }

  let basicPermissionSetData = allPermissionSets?.find(
    (item) => item.permission_set_name === 'Basic'
  )

  const handleCheck = (selectedValue: PermissionSetType) => {
    if (
      isUamPermissionSetDisabledForProfiles(
        selectedValue.permission_set_name,
        targetUserProfiles
      )
    ) {
      return
    }
    setCheckedPermissionSets((prev) => {
      if (prev[selectedValue.permission_set_id]) {
        const updatedState = { ...prev }
        delete updatedState[selectedValue.permission_set_id]
        return updatedState
      } else {
        return { ...prev, [selectedValue.permission_set_id]: selectedValue }
      }
    })
  }

  const handleAddProgram = (
    selectedProgramPermission: ProgramPermissionsType
  ) => {
    const basicSelected = selectedPermissionSets?.findIndex(
      (item) => item.permission_set_name === 'Basic'
    )
    if (basicSelected === -1 && !isEmpty(basicPermissionSetData)) {
      selectedPermissionSets.push(basicPermissionSetData)
    }
    const permission_sets = selectedPermissionSets
      ?.map((permission_set) => permission_set.permission_set_name)
      ?.join(', ')
    const programIndex = programPermissionSets?.findIndex(
      (programPermissionSetData) =>
        programPermissionSetData.program_id ===
        selectedProgramPermission.program_id
    )

    const permissionSets = selectedProgramPermission.permission_sets?.map(
      (item) => {
        return {
          permission_set_name: item.permission_set_name,
          permission_set_id: item.permission_set_id,
        }
      }
    )
    if (programIndex > -1) {
      programPermissionSets[programIndex] = {
        ...selectedProgramPermission,
        permission_sets: permissionSets,
        enabled: true,
      }
      setProgramPermissionSets([...programPermissionSets])
      tableData[programIndex] = {
        ...selectedProgramPermission,
        permission_sets,
      }
      setTableData([...tableData])
    } else {
      setProgramPermissionSets([
        ...programPermissionSets,
        {
          ...selectedProgramPermission,
          permission_sets: permissionSets,
          enabled: true,
        },
      ])
      setTableData([
        ...tableData,
        { ...selectedProgramPermission, permission_sets },
      ])
    }
  }

  const handleClearProgram = () => {
    setCheckedPermissionSets({})
  }

  const columns: TableColumnType<DataTypeStore>[] = [
    ...(!isGlobalSet
      ? [
          {
            header: PERMISSION_ASSIGNMENT.PROGRAM,
            accessor: PERMISSION_ASSIGNMENT.PROGRAM_NAME_KEY,
            minWidth: '1',
          },
        ]
      : []),
    {
      header: PERMISSION_ASSIGNMENT.PERMISSION_SETS,
      accessor: PERMISSION_ASSIGNMENT.PERMISSION_SET_KEY,
    },

    ...(actionMode !== NAMESPACES.VIEW_KEY
      ? [
          {
            header: PERMISSION_ASSIGNMENT.ACTIONS,
            accessor: PERMISSION_ASSIGNMENT.ACTIONS_KEY,
            type: columnTypesConst?.COMPONENT,
            renderHeader: () => (
              <div className="text-center">{PERMISSION_ASSIGNMENT.ACTIONS}</div>
            ),
            renderComponent: (row: DataTypeStore, rowIndex: number) => (
              <div
                className={`px-4 py-1 z-10 max-w-[16rem] min-w-fit action-col ${
                  row.hidden ? 'hidden-data' : ''
                } ${row.noAction ? 'hidden' : ''}`}
              >
                <div className="flex space-x-2  justify-center items-center relative ">
                  <span
                    data-qa-id={`${NAMESPACES.EDIT_KEY}_${rowIndex}`}
                    className={` p-2 block rounded  ${
                      !row.enabled ? '' : 'hover:bg-secondary-25 cursor-pointer'
                    }`}
                    onClick={(e) => {
                      e?.stopPropagation()
                      if (!row.enabled) {
                        return
                      } else {
                        editProgramPermissionData(row?.program_id as string)
                      }
                    }}
                    tabIndex={TAB_INDEX_DEFAULT}
                    onKeyDown={(e) => {
                      if (
                        e?.key === ShortcutKeys.Enter ||
                        e?.key === ShortcutKeys.Space
                      ) {
                        e?.stopPropagation()
                        e?.preventDefault()
                        if (!row.enabled) {
                          return
                        } else {
                          editProgramPermissionData(row?.program_id as string)
                        }
                      }
                    }}
                  >
                    <Tooltip content={NAMESPACES.EDIT}>
                      <span
                        className={` ${
                          !row.enabled
                            ? 'svg-path-stroke-accent-200'
                            : 'svg-path-stroke-secondary-hover svg-path-stroke-accent-500'
                        }`}
                      >
                        <SvgEdit height={'0.9rem'} width={'0.9rem'} />
                      </span>
                    </Tooltip>
                  </span>
                  <span>
                    <ToggleButton
                      data-qa-id={`${NAMESPACES.TOGGLE}_${rowIndex}`}
                      name={NAMESPACES.TOGGLE}
                      checked={!!row.enabled}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => {
                        handleToggleChange(row, rowIndex)
                      }}
                    />
                  </span>
                </div>
              </div>
            ),
          },
        ]
      : []),
  ]

  const editProgramPermissionData = (id: string) => {
    const data = programPermissionSets?.find((item) => item.program_id === id)
    const defaultPermissionSets: Record<string, PermissionSetType> = {}
    data?.permission_sets?.forEach((permissionSet) => {
      defaultPermissionSets[permissionSet.permission_set_id] = {
        ...permissionSet,
        permissions: [],
        permission_set_id: permissionSet.permission_set_id,
      }
    })
    if (data) {
      setSelectedProgram({
        program_id: data?.program_id?.toString(),
        program_name: data.program_name,
      })
      setCheckedPermissionSets({ ...defaultPermissionSets })
    }
  }

  const handleToggleChange = (row: DataTypeStore, rowIndex: number) => {
    if (row.enabled) {
      setToggleModalOpen(true)
    } else {
      toggleProgramPermissionData(row, rowIndex)
    }
    setRowInstance({ row, rowIndex })
  }

  const toggleProgramPermissionData = (
    rowData: DataTypeStore,
    index: number
  ) => {
    const updatedTableData = [...tableData]
    const updatedProgramPermissionSets = [...programPermissionSets]
    updatedTableData[index] = {
      ...updatedTableData[index],
      enabled: !rowData.enabled,
    }
    updatedProgramPermissionSets[index] = {
      ...updatedProgramPermissionSets[index],
      enabled: !rowData.enabled,
    }
    setTableData(updatedTableData)
    setProgramPermissionSets(updatedProgramPermissionSets)
  }

  const handleProgramChange = (e: DropdownType<DataTypeStore, string>) => {
    const existingProgram = programPermissionSets?.find(
      (programPermissionData) => programPermissionData.program_id === e.value
    )
    if (existingProgram) {
      const defaultSelectedPermissionSets: Record<string, PermissionSetType> =
        {}
      existingProgram.permission_sets?.forEach((item) => {
        defaultSelectedPermissionSets[item.permission_set_id] = {
          ...item,
          permissions: [],
          permission_set_id: item.permission_set_id,
        }
      })
      setCheckedPermissionSets(defaultSelectedPermissionSets)
    } else if (selectedProgram) setCheckedPermissionSets({})
    setSelectedProgram({ program_id: e.value, program_name: e.label })
  }

  const fetchTableData = async () => {
    try {
      dispatch(showLoader())
      let updatedData
      if (isGlobalSet) {
        const response = await getPermissionSetsForGlobal({ id: userId ?? '' })
        updatedData = [
          {
            permission_sets: response?.data
              .map(
                (item: (typeof response.data)[0]) => item.permission_set_name
              )
              .join(', '),
          },
        ]
        if (
          actionMode === NAMESPACES.EDIT_KEY ||
          actionMode === NAMESPACES.CLONE_KEY
        ) {
          const checkedSets: Record<string, PermissionSetType> = {}
          response?.data?.forEach(
            (permissionSet: (typeof response.data)[0]) => {
              checkedSets[permissionSet.id] = {
                ...permissionSet,
                permissions: [],
                permission_set_id: permissionSet.id,
              }
            }
          )
          setCheckedPermissionSets({ ...checkedSets })
        }
      } else {
        const response = await getProgramsAndPermissionSets({
          id: userId ?? '',
        })
        updatedData = response?.data?.programs.map(
          (item: (typeof response.data)[0]) => ({
            ...item,
            program_id: item.program_id.toString(),
            permission_sets: item.permission_sets
              .map(
                (per: (typeof item.permission_sets)[0]) =>
                  per.permission_set_name
              )
              .join(', '),
            disabled: !item.enabled,
            hidden: canActAsAccessManagerOnPa
              ? false
              : !permissionObject.Permission_Set_Assignment_Manage(
                  programPermissions,
                  PERMISSION_MODULES.PERMISSION_ASSIGNMENT,
                  'View Only',
                  item.program_id?.toString()
                ),
            noAction: canActAsAccessManagerOnPa
              ? false
              : !permissionObject.Permission_Set_Assignment_Manage(
                  programPermissions,
                  PERMISSION_MODULES.PERMISSION_ASSIGNMENT,
                  'Manage',
                  item.program_id?.toString()
                ),
          })
        )
        const permissionData = response?.data?.programs.map(
          (item: (typeof response.data)[0]) => ({
            program_name: item.program_name,
            program_id: item.program_id.toString(),
            permission_sets: item.permission_sets.map(
              (per: (typeof item.permission_sets)[0]) => ({
                permission_set_id: per?.id,
                permission_set_name: per?.permission_set_name,
              })
            ),
            enabled: item.enabled,
          })
        )
        setProgramPermissionSets(permissionData)
      }
      setTableData(updatedData)
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const fetchPermissionSets = async () => {
    try {
      const response = await getPermissionSetsList()
      if (response?.data) {
        const permissionSetData = response?.data?.filter(
          (item: PermissionSetType) =>
            item.set_type !== PERMISSION_ASSIGNMENT.ADMIN &&
            item.set_type !== PERMISSION_ASSIGNMENT.GLOBAL
        )
        basicPermissionSetData = permissionSetData?.find(
          (item: PermissionSetType) => item.permission_set_name === 'Basic'
        )
        const globalPermissionSetData = response?.data?.filter(
          (item: PermissionSetType) =>
            item.set_type === PERMISSION_ASSIGNMENT.GLOBAL
        )
        if (isGlobalSet) {
          setAllPermissionSets(globalPermissionSetData)
        } else {
          setAllPermissionSets(permissionSetData)
        }
      }
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    fetchPermissionSets()
    if (
      actionMode === NAMESPACES.VIEW_KEY ||
      actionMode === NAMESPACES.EDIT_KEY ||
      actionMode === NAMESPACES.CLONE_KEY
    ) {
      fetchTableData()
    }
  }, [actionMode])

  useEffect(() => {
    setTableData([])
  }, [programPermissions])

  return (
    <>
      {actionMode !== NAMESPACES.VIEW_KEY ? (
        <div className="grid grid-cols-[2fr,3fr] flex-1 min-h-[25rem] gap-3 border-b p-3">
          <AssignPermissionSidebar
            checkedPermissionSets={checkedPermissionSets}
            handleCheck={handleCheck}
            allPermissionSets={allPermissionSets}
            targetUserProfiles={targetUserProfiles}
          />
          {!isGlobalSet ? (
            <AssignPermissionSetRightPanel
              selectedPermissionSets={selectedPermissionSets}
              handleClearChip={handleClearChip}
              handleAddProgram={handleAddProgram}
              handleClearProgram={handleClearProgram}
              selectedProgram={selectedProgram}
              handleProgramChange={handleProgramChange}
              programOptions={programOptions}
            />
          ) : (
            <div className="flex flex-col font-primary text-base font-normal items-center text-gray-700">
              <SvgGlobalSet />
              {PERMISSION_CONSTANTS.GLOBAL_PERMISSION_MESSAGE}
            </div>
          )}
        </div>
      ) : null}
      {!isGlobalSet || actionMode === NAMESPACES.VIEW_KEY ? (
        isEmpty(tableData) ? (
          <div className="flex flex-col justify-center items-center p-3 custom-shadow rounded gap-3 mb-3">
            <SvgEntity />
            <div className="font-primary font-medium text-xs/5">
              {PERMISSION_ASSIGNMENT.PROGRAM_PERMISSION_SET_TABLE_EMPTY_MESSAGE}
            </div>
          </div>
        ) : (
          <div className="p-3">
            <div className="border border-border-100 rounded p-1 mb-3 hide-disabled-row">
              <CustomTable
                columns={columns}
                data={tableData as unknown as DataTypeStore[]}
                emptyDataMessage="No permission sets found"
              />
            </div>
          </div>
        )
      ) : null}
      {toggleModalOpen && (
        <CustomConfirmationModal
          open={toggleModalOpen}
          handleClose={() => setToggleModalOpen(false)}
          width={'w-[30rem]'}
          title={PERMISSION_ASSIGNMENT.DISABLE_MODAL_TITLE}
          onConfirm={() => {
            toggleProgramPermissionData(
              rowInstance?.row as unknown as DataTypeStore,
              rowInstance?.rowIndex ?? -1
            )
            setToggleModalOpen(false)
          }}
          message={PERMISSION_ASSIGNMENT.DISABLE_MODAL_MESSAGE}
        />
      )}
    </>
  )
}
